import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { parsePaymentQuery } from './utils/query';
import { usePaymentFlow } from './hooks/usePaymentFlow';
import type { FlowStep, InfoDetail, OperationStatusResponse } from './universal-flow/types';
import { ActionForm } from './app/components/ActionForm';
import { StatusBanner } from './app/components/StatusBanner';
import { getMessages, resolveLocale, type LocaleKey, type Messages } from './i18n';

function renderAwaitingDescription(
  response: OperationStatusResponse,
  fallback: string
): JSX.Element | null {
  const message = extractFlowMessage(response);
  if ((message === null || message === undefined || message === '') && (fallback === null || fallback === undefined || fallback === '')) {
    return null;
  }

  return (
    <p style={{ marginBottom: '16px' }}>
      {message ?? fallback}
    </p>
  );
}

const FINAL_STATUSES = new Set([2, 3, 4, 5, 6, 7]);

export default function App() {
  const query = useMemo(() => parsePaymentQuery(window.location), []);
  const locale = useMemo<LocaleKey>(() => resolveLocale(), []);
  const t = useMemo<Messages>(() => getMessages(locale), [locale]);
  const [flowState, actions] = usePaymentFlow(query, t.status.infoTitleFallback);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const saved = localStorage.getItem('theme');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });

  useEffect(() => {
    if (flowState.status === 'ready' && flowState.step.kind === 'redirect') {
      const url = flowState.step.action.url;
      if (url !== null && url !== undefined && url !== '') {
        window.location.href = url;
      }
    }
  }, [flowState]);

  useEffect(() => {
    document.body.className = `theme-${theme}`;
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const onSubmit = useCallback(
    async (payload: Record<string, string>) => {
      setSubmitting(true);
      try {
        await actions.submitExtra(payload);
      } finally {
        setSubmitting(false);
      }
    },
    [actions]
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await actions.refresh();
    } finally {
      setRefreshing(false);
    }
  }, [actions]);

  return (
    <div className="app-shell">
      <div className="card" style={{ position: 'relative' }}>
        <button
          onClick={toggleTheme}
          className="theme-toggle-btn"
          aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        >
          {theme === 'dark' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"/>
              <line x1="12" y1="1" x2="12" y2="3"/>
              <line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/>
              <line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          )}
        </button>
        <header style={{ marginBottom: '24px' }}>
          <h1>{t.title}</h1>
          <p className="card-subtitle">{t.subtitle}</p>
        </header>

        {renderContent(flowState, { onSubmit, submitting, onRefresh, refreshing }, t, locale)}
      </div>
    </div>
  );
}

type RenderArgs = {
  onSubmit: (payload: Record<string, string>) => Promise<void>;
  submitting: boolean;
  onRefresh: () => Promise<void>;
  refreshing: boolean;
};

function renderContent(
  flowState: ReturnType<typeof usePaymentFlow>[0],
  args: RenderArgs,
  t: Messages,
  locale: LocaleKey
) {
  if (flowState.status === 'bootstrapping') {
    return <Loader />;
  }

  if (flowState.status === 'error') {
    return (
      <ErrorState
        message={flowState.message}
        details={flowState.details}
        canRetry={flowState.canRetry !== false}
        onRetry={args.onRefresh}
        retrying={args.refreshing}
        labels={t.error}
      />
    );
  }

  const { step } = flowState;

  if (step.kind === 'loading') {
    return <Loader />;
  }

  return (
    <ReadyState
      step={step}
      submitting={args.submitting}
      refreshing={args.refreshing}
      onSubmit={args.onSubmit}
      onRefresh={args.onRefresh}
      labels={t}
      locale={locale}
    />
  );
}

type ReadyFlowStep = Exclude<FlowStep, { kind: 'loading' }>;

type ReadyStateProps = RenderArgs & {
  step: ReadyFlowStep;
  labels: Messages;
  locale: LocaleKey;
};

type SummaryFields = {
  amountDisplay?: string;
  currency?: string;
  primaryId?: string;
  secondaryId?: string;
  providerName?: string;
  merchantName?: string;
  paymentMethod?: string;
  transactionType?: string;
  redirectUrl?: string;
};

function ReadyState({
  step,
  submitting,
  refreshing,
  onSubmit,
  onRefresh,
  labels,
  locale
}: ReadyStateProps) {
  const summaryRef = { current: {} as SummaryFields };
  const summary = useMemo<SummaryFields>(() => {
    const base: SummaryFields = { ...summaryRef.current };
    const current = collectSummaryFields(step, locale);

    (Object.keys(current) as Array<keyof SummaryFields>).forEach((key) => {
      const value = current[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        base[key] = value;
      }
    });

    summaryRef.current = base;
    return base;
  }, [step, locale]);

  const [bannerVisible, setBannerVisible] = useState(false);

  return (
    <div className="flow">
      <div style={{ position: 'relative' }}>
        <OrderSummary
          summary={summary}
          labels={labels.order}
          bannerVisible={bannerVisible}
          onToggleBanner={() => setBannerVisible((prev) => !prev)}
        />
        <StatusBanner
          response={step.response}
          labels={labels.statusBanner}
          visible={bannerVisible}
        />
      </div>
      <div style={{ marginTop: '32px' }}>
        {renderStep(step, { submitting, refreshing, onSubmit, onRefresh }, labels, summary, locale)}
      </div>
      {summary.redirectUrl &&
      typeof step.response.status === 'number' &&
      FINAL_STATUSES.has(step.response.status) ? (
        <div style={{ marginTop: '32px' }}>
          <FinalRedirectNotice
            url={summary.redirectUrl}
            labels={{
              action: labels.status.redirectLinkAction
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

type StepRenderArgs = {
  submitting: boolean;
  refreshing: boolean;
  onSubmit: (payload: Record<string, string>) => Promise<void>;
  onRefresh: () => Promise<void>;
};

type StatusTone = 'success' | 'error' | 'pending' | 'info' | 'action';

type StatusHeroProps = {
  tone: StatusTone;
  title: string;
  description?: string;
};

const STATUS_VISUALS: Record<
  StatusTone,
  { icon: JSX.Element; accent: string; background: string; border: string }
> = {
  success: {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
    accent: '#4ade80',
    background: 'rgba(74, 222, 128, 0.08)',
    border: '1px solid rgba(74, 222, 128, 0.4)'
  },
  error: {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
    accent: '#f87171',
    background: 'rgba(248, 113, 113, 0.08)',
    border: '1px solid rgba(248, 113, 113, 0.35)'
  },
  pending: {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    accent: '#facc15',
    background: 'rgba(250, 204, 21, 0.08)',
    border: '1px solid rgba(250, 204, 21, 0.35)'
  },
  info: {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
    ),
    accent: '#60a5fa',
    background: 'rgba(96, 165, 250, 0.08)',
    border: '1px solid rgba(96, 165, 250, 0.35)'
  },
  action: {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>
    ),
    accent: '#a855f7',
    background: 'rgba(168, 85, 247, 0.08)',
    border: '1px solid rgba(168, 85, 247, 0.35)'
  }
};

function StatusHero({ tone, title, description }: StatusHeroProps) {
  const visuals = STATUS_VISUALS[tone];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px 18px',
        marginBottom: description ? '14px' : '20px',
        borderRadius: '14px',
        background: visuals.background,
        border: visuals.border
      }}
    >
      <span
        aria-hidden="true"
        className="status-icon"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: visuals.accent,
          flexShrink: 0
        }}
      >
        {visuals.icon}
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: description ? '6px' : '0' }}>
        <h2
          style={{
            margin: 0,
            color: visuals.accent,
            fontSize: '1.35rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            lineHeight: 1.25
          }}
        >
          {title}
        </h2>
        {description ? (
          <p
            className="status-description"
            style={{
              margin: 0,
              fontSize: '0.95rem',
              lineHeight: 1.45
            }}
          >
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function renderStep(step: ReadyFlowStep, args: StepRenderArgs, labels: Messages, summary: SummaryFields, locale: LocaleKey) {
  const transactionTypeDictionary = labels.order.transactionTypes as Record<string, string> | undefined;
  const paymentMethodDictionary = labels.order.paymentMethods as Record<string, string> | undefined;

  const formatDetailValue = (detail: InfoDetail): string | undefined => {
    const lastKeySegment = detail.key.split('.').pop()?.replace(/\[\d+\]/g, '') ?? detail.key;
    const value = detail.value?.trim();
    if (!value) {
      return undefined;
    }

    if (transactionTypeDictionary && (lastKeySegment === 'transaction_type' || lastKeySegment === 'transaction_type_id')) {
      return transactionTypeDictionary[value] ?? value;
    }

    if (
      paymentMethodDictionary &&
      (lastKeySegment === 'payment_method' || lastKeySegment === 'payment_method_id' || lastKeySegment === 'method')
    ) {
      return paymentMethodDictionary[value] ?? value;
    }

    return undefined;
  };

  switch (step.kind) {
    case 'success':
      return (
        <div>
          <StatusHero
            tone="success"
            title={labels.status.successTitle}
            description={labels.status.successDescriptionPrefix || undefined}
          />
        </div>
      );
    case 'failed':
      return (
        <div>
          <StatusHero
            tone="error"
            title={labels.status.failedTitle}
            description={labels.status.failedDescriptionPrefix || undefined}
          />
        </div>
      );
    case 'awaiting-data':
      return (
        <div>
          <StatusHero
            tone="info"
            title={labels.status.awaitingTitle}
            description={undefined}
          />
          {renderAwaitingDescription(step.response, labels.status.awaitingDescription)}
          <button
            style={{ marginTop: '18px' }}
            onClick={() => void args.onRefresh()}
            disabled={args.refreshing}
          >
            {args.refreshing ? (
              <>
                <span className="button-spinner" aria-hidden="true" />
                {labels.status.refreshBusy}
              </>
            ) : (
              labels.status.refreshButton
            )}
          </button>
        </div>
      );
    case 'form':
      return (
        <div>
          <StatusHero
            tone="action"
            title={step.action.title ?? labels.status.formTitleFallback}
            description={step.action.description ?? undefined}
          />
          <ActionForm
            action={step.action}
            submitting={args.submitting}
            onSubmit={args.onSubmit}
            labels={labels.form}
            initialValues={extractInitialFormValues(step)}
            locale={locale}
          />
        </div>
      );
    case 'redirect':
      return (
        <div>
          <StatusHero
            tone="info"
            title={labels.status.redirectTitle}
            description={labels.status.redirectDescription}
          />
          <button
            style={{ marginTop: '18px' }}
            onClick={() => {
              const targetUrl = step.action.url;
              if (typeof targetUrl === 'string' && targetUrl.length > 0) {
                window.location.assign(targetUrl);
              }
            }}
            disabled={args.submitting || typeof step.action.url !== 'string'}
          >
            {labels.status.redirectButton}
          </button>
        </div>
      );
    case 'info': {
      const isPending = step.response.status === 1;
      const tone: StatusTone = isPending ? 'pending' : 'info';
      const title = isPending
        ? labels.status.pendingTitle
        : step.action?.title ?? labels.status.infoTitleFallback;
      const description = isPending ? labels.status.pendingDescription : step.action?.description;
      const buttonLabel = isPending ? labels.status.refreshButton : labels.status.infoButton;
      const busyLabel = isPending ? labels.status.refreshBusy : labels.status.infoBusy;

      return (
        <div>
          <StatusHero tone={tone} title={title} description={description} />
          {step.details && step.details.length > 0 ? (
            <DetailsList
              details={step.details}
              labelMap={labels.infoDetails}
              hiddenValues={summary.secondaryId ? [summary.secondaryId] : undefined}
              valueFormatter={formatDetailValue}
            />
          ) : null}
          <button
            style={{ marginTop: '18px' }}
            onClick={() => void args.onRefresh()}
            disabled={args.refreshing}
          >
            {args.refreshing ? (
              <>
                <span className="button-spinner" aria-hidden="true" />
                {busyLabel}
              </>
            ) : (
              buttonLabel
            )}
          </button>
        </div>
      );
    }
    case 'pending':
      return (
        <div>
          <StatusHero
            tone="pending"
            title={labels.status.pendingTitle}
            description={labels.status.pendingDescription}
          />
          <button
            style={{ marginTop: '18px' }}
            onClick={() => void args.onRefresh()}
            disabled={args.refreshing}
          >
            {args.refreshing ? (
              <>
                <span className="button-spinner" aria-hidden="true" />
                {labels.status.refreshBusy}
              </>
            ) : (
              labels.status.refreshButton
            )}
          </button>
        </div>
      );
    default:
      return <Loader />;
  }
}

function Loader() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '180px' }}>
      <div className="spinner" style={{ width: '56px', height: '56px', position: 'relative' }}>
        <span
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '4px solid rgba(65, 105, 225, 0.2)'
          }}
        />
        <span
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '4px solid transparent',
            borderTopColor: '#ff6b35',
            animation: 'spin 1s linear infinite'
          }}
        />
      </div>
    </div>
  );
}

type ErrorStateProps = {
  message: string;
  details?: string;
  canRetry: boolean;
  onRetry: () => Promise<void>;
  retrying: boolean;
  labels: {
    title: string;
    summary: string;
    showDetails: string;
    hideDetails: string;
    retry: string;
    retrying: string;
  };
};

function ErrorState({ message, details, canRetry, onRetry, retrying, labels }: ErrorStateProps) {
  const [showDetails, setShowDetails] = useState(false);
  const displayMessage = message?.trim().length ? message : labels.summary;

  return (
    <div>
      <h2>{labels.title}</h2>
      <p style={{ color: '#f87171', fontSize: '0.95rem' }}>{displayMessage}</p>
      <div style={{ marginBottom: '12px' }}>
        <button
          type="button"
          onClick={() => setShowDetails((prev) => !prev)}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(148, 163, 184, 0.9)',
            cursor: 'pointer',
            padding: 0,
            textDecoration: 'underline',
            fontSize: '0.85rem'
          }}
        >
          {showDetails ? labels.hideDetails : labels.showDetails}
        </button>
      </div>
      {showDetails ? (
        <pre
          style={{
            background: 'rgba(148, 163, 184, 0.08)',
            padding: '12px',
            borderRadius: '8px',
            overflowX: 'auto',
            marginBottom: '16px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.85rem',
            lineHeight: 1.4
          }}
        >
          {details ?? formatMessage(message)}
        </pre>
      ) : null}
      {canRetry ? (
        <button onClick={() => void onRetry()} disabled={retrying}>
          {retrying ? (
            <>
              <span className="button-spinner" aria-hidden="true" />
              {labels.retrying}
            </>
          ) : (
            labels.retry
          )}
        </button>
      ) : null}
    </div>
  );
}

type DetailsListProps = {
  details: InfoDetail[];
  labelMap?: Record<string, string>;
  hiddenValues?: string[];
  valueFormatter?: (detail: InfoDetail) => string | undefined;
};

function DetailsList({ details, labelMap, hiddenValues, valueFormatter }: DetailsListProps) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const hiddenValueSet = useMemo(() => {
    return new Set(
      (hiddenValues ?? []).filter(
        (value): value is string => typeof value === 'string' && value.trim().length > 0
      )
    );
  }, [hiddenValues]);

  const handleCopy = useCallback((rowKey: string, content: string) => {
    const copy = async () => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(content);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = content;
          textarea.setAttribute('readonly', 'true');
          textarea.style.position = 'absolute';
          textarea.style.left = '-9999px';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        setCopiedKey(rowKey);
        window.setTimeout(() => {
          setCopiedKey((current) => (current === rowKey ? null : current));
        }, 1500);
      } catch (error) {
        console.warn('Copy failed', error);
      }
    };

    void copy();
  }, []);

  return (
    <dl className="info-details">
      {details.map((detail) => (
        <div
          key={detail.key}
          className="info-details__row"
          style={{
            display:
              detail.key.toLowerCase().endsWith('order_id2') || hiddenValueSet.has(detail.value)
                ? 'none'
                : undefined
          }}
        >
          <dt>{resolveDetailLabel(detail, labelMap)}</dt>
          <dd
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'default'
            }}
          >
            <span>{valueFormatter?.(detail) ?? detail.value}</span>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const valueToCopy = valueFormatter?.(detail) ?? detail.value;
                handleCopy(detail.key, valueToCopy);
              }}
              className={`copy-button ${copiedKey === detail.key ? 'copied' : ''}`}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                fontSize: '1rem'
              }}
              aria-label="Copy value"
            >
              {copiedKey === detail.key ? '✓' : '⧉'}
            </button>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function resolveDetailLabel(detail: InfoDetail, labelMap?: Record<string, string>): string {
  if (!labelMap) {
    return detail.label || detail.key;
  }

  const candidates = [
    detail.key,
    detail.key.toLowerCase(),
    detail.label,
    detail.label.toLowerCase()
  ].filter(Boolean) as string[];

  for (const candidate of candidates) {
    const translation = labelMap[candidate];
    if (typeof translation === 'string' && translation.trim().length > 0) {
      return translation;
    }
  }

  return detail.label || detail.key;
}
type OrderSummaryProps = {
  summary: SummaryFields;
  labels: Messages['order'];
  bannerVisible: boolean;
  onToggleBanner: () => void;
};

function OrderSummary({ summary, labels, bannerVisible, onToggleBanner }: OrderSummaryProps) {
  const {
    amountDisplay,
    currency,
    primaryId,
    secondaryId,
    providerName,
    merchantName,
    paymentMethod,
    transactionType
  } = summary;

  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const transactionTypeDictionary = labels.transactionTypes as Record<string, string> | undefined;
  const paymentMethodDictionary = labels.paymentMethods as Record<string, string> | undefined;
  const transactionTypeDisplay =
    transactionType && transactionTypeDictionary
      ? transactionTypeDictionary[transactionType] ?? transactionType
      : transactionType ?? null;
  const paymentMethodDisplay =
    paymentMethod && paymentMethodDictionary
      ? paymentMethodDictionary[paymentMethod] ?? paymentMethod
      : paymentMethod ?? null;

  const detailRows: Array<{ key: string; label: string; value: string; copyable?: boolean }> = [];

  if (currency && (!amountDisplay || !amountDisplay.includes(currency))) {
    detailRows.push({
      key: 'currency',
      label: labels.currencyLabel,
      value: currency,
      copyable: false
    });
  }

  if (primaryId) {
    detailRows.push({
      key: 'primaryId',
      label: labels.primaryLabel,
      value: primaryId,
      copyable: true
    });
  }

  if (secondaryId) {
    detailRows.push({
      key: 'secondaryId',
      label: labels.secondaryLabel,
      value: secondaryId,
      copyable: false
    });
  }

  if (providerName) {
    detailRows.push({
      key: 'providerName',
      label: labels.providerLabel,
      value: providerName,
      copyable: false
    });
  }

  if (merchantName) {
    detailRows.push({
      key: 'merchantName',
      label: labels.merchantLabel,
      value: merchantName,
      copyable: false
    });
  }

  // paymentMethod отображаем отдельно под суммой

  const handleCopy = useCallback((rowKey: string, content: string) => {
    const copy = async () => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(content);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = content;
          textarea.setAttribute('readonly', 'true');
          textarea.style.position = 'absolute';
          textarea.style.left = '-9999px';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        setCopiedKey(rowKey);
        window.setTimeout(() => {
          setCopiedKey((current) => (current === rowKey ? null : current));
        }, 1500);
      } catch (error) {
        console.warn('Copy failed', error);
      }
    };

    void copy();
  }, []);

  return (
    <div className="order-summary" style={{ marginBottom: '24px' }}>
      {amountDisplay ? (
        <div
          className="order-summary-amount"
          style={{
            fontSize: '1.6rem',
            fontWeight: 600,
            marginBottom: detailRows.length > 0 ? '12px' : 0,
            lineHeight: 1.22
          }}
        >
          <span>{amountDisplay}</span>
        </div>
      ) : null}
      {transactionTypeDisplay || paymentMethodDisplay ? (
        <div
          style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: detailRows.length > 0 ? '12px' : 0
          }}
        >
          {transactionTypeDisplay ? (
            <span
              className="order-summary-transaction-type"
              style={{
                fontSize: '0.92rem',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                fontWeight: 600
              }}
            >
              {transactionTypeDisplay}
            </span>
          ) : null}
          {paymentMethodDisplay ? (
            <span
              className="order-summary-payment-method"
              style={{
                fontSize: '0.95rem',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: '9999px',
                lineHeight: 1.1
              }}
            >
              {paymentMethodDisplay}
            </span>
          ) : null}
        </div>
      ) : null}
      {detailRows.length > 0 ? (
        <>
          <dl className="info-details" style={{ position: 'relative', paddingTop: '40px' }}>
            <div
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px'
              }}
            >
              <button
                type="button"
                onClick={onToggleBanner}
                className={`info-toggle-btn ${bannerVisible ? 'active' : ''}`}
                title={bannerVisible ? labels.statusToggleHide : labels.statusToggleShow}
              >
                {bannerVisible ? '×' : 'i'}
              </button>
            </div>
            {detailRows.map((detail) => (
        <div key={detail.key} className="info-details__row">
                <dt>{detail.label}</dt>
                <dd
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    userSelect: detail.copyable ? 'text' : 'text'
                  }}
                >
                  <span>{detail.value}</span>
                  {detail.copyable ? (
                    <button
                      type="button"
                      onClick={() => handleCopy(detail.key, detail.value)}
                      title={copiedKey === detail.key ? 'Copied' : 'Copy'}
                      className={`copy-button ${copiedKey === detail.key ? 'copied' : ''}`}
                      style={{
                        border: 'none',
                        background: 'none',
                        padding: 0,
                        fontSize: '1rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1
                      }}
                    >
                      {copiedKey === detail.key ? '✓' : '⧉'}
                    </button>
                  ) : null}
                </dd>
        </div>
      ))}
    </dl>
        </>
      ) : null}
    </div>
  );
}

type FinalRedirectNoticeProps = {
  url: string;
  labels: {
    action: string;
  };
};

function FinalRedirectNotice({ url, labels }: FinalRedirectNoticeProps) {
  return (
    <div
      className="final-redirect-container"
      style={{
        marginTop: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '10px 12px',
        fontSize: '0.9rem'
      }}
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="final-redirect-link"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontWeight: 500,
          fontSize: '0.9rem',
          textDecoration: 'none'
        }}
      >
        <span
          style={{
            textDecoration: 'underline'
          }}
        >
          {labels.action}
        </span>
        <span aria-hidden="true" style={{ fontSize: '1rem', lineHeight: 1 }}>
          ↗
        </span>
      </a>
    </div>
  );
}

function extractInitialFormValues(step: ReadyFlowStep): Record<string, unknown> | undefined {
  if (step.kind !== 'form') {
    return undefined;
  }

  const sources = gatherSources(step);
  if (sources.length === 0) {
    return undefined;
  }

  const result: Record<string, unknown> = {};
  const fieldNames = Object.keys(step.action.fields ?? {});

  for (const name of fieldNames) {
    for (const source of sources) {
      const nested = findNestedValue(source, [name]);
      if (isUsableValue(nested)) {
        result[name] = nested;
        break;
      }
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

function extractFlowMessage(response: OperationStatusResponse): string | undefined {
  if (response === null || response === undefined || typeof response !== 'object') {
    return undefined;
  }

  const directCandidates = [
    response.result?.message,
    response.provider_result?.message,
    response.result?.error,
    response.provider_result?.error,
    response.result?.code,
    response.provider_result?.code
  ];

  for (const candidate of directCandidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }

  const extra = response.extra;
  if (extra && typeof extra === 'object') {
    const extraCandidate = [
      extra.message,
      extra.error,
      extra.reason,
      extra.description
    ].find((value): value is string => typeof value === 'string' && value.trim().length > 0);

    if (extraCandidate) {
      return extraCandidate;
    }

    if (Array.isArray(extra.errors)) {
      for (const entry of extra.errors) {
        const message = pickErrorMessage(entry);
        if (message && message.trim().length > 0) {
          return message;
        }
      }
    }
  }

  if (Array.isArray(response.errors)) {
    for (const entry of response.errors) {
      const message = pickErrorMessage(entry);
      if (message && message.trim().length > 0) {
        return message;
      }
    }
  }

  return undefined;
}

function isUsableValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  return false;
}

function formatAmount(
  amountNumeric: number | undefined,
  amountRaw: string | undefined,
  currency: string | undefined,
  locale: LocaleKey
): string | undefined {
  if (typeof amountNumeric === 'number' && Number.isFinite(amountNumeric)) {
    try {
      if (currency) {
        const hasMinorUnits = Math.abs(amountNumeric - Math.trunc(amountNumeric)) > 1e-8;
        const currencyOptions: Intl.NumberFormatOptions = {
          style: 'currency',
          currency
        };
        if (!hasMinorUnits) {
          currencyOptions.minimumFractionDigits = 0;
          currencyOptions.maximumFractionDigits = 0;
        } else {
          currencyOptions.minimumFractionDigits = 2;
          currencyOptions.maximumFractionDigits = 2;
        }
        return new Intl.NumberFormat(locale, currencyOptions).format(amountNumeric);
      }
      const hasFraction = Math.abs(amountNumeric - Math.trunc(amountNumeric)) > 1e-8;
      const decimalOptions: Intl.NumberFormatOptions = {
        style: 'decimal',
        maximumFractionDigits: hasFraction ? 2 : 0,
        minimumFractionDigits: hasFraction ? 2 : 0
      };
      return new Intl.NumberFormat(locale, decimalOptions).format(amountNumeric);
    } catch {
      // ignore format errors and fallback below
    }
  }

  if (amountRaw && amountRaw.trim().length > 0) {
    if (currency && !amountRaw.toLowerCase().includes(currency.toLowerCase())) {
      return `${amountRaw} ${currency}`;
    }
    return amountRaw;
  }

  return undefined;
}

function findNestedValue(
  source: Record<string, unknown> | Array<unknown> | undefined,
  path: string[],
  visited = new Set<unknown>()
): unknown {
  if (!source || typeof source !== 'object') {
    return undefined;
  }
  if (visited.has(source)) {
    return undefined;
  }
  visited.add(source);

  if (Array.isArray(source)) {
    for (const item of source) {
      if (item && typeof item === 'object') {
        const nested = findNestedValue(item as Record<string, unknown>, path, visited);
        if (isUsableValue(nested)) {
          return nested;
        }
      }
    }
    return undefined;
  }

  if (path.length === 0) {
    return undefined;
  }

  const [current, ...rest] = path;
  const direct = (source as Record<string, unknown>)[current];
  if (rest.length === 0) {
    if (direct !== undefined) {
      return direct;
    }
  } else if (direct && typeof direct === 'object') {
    const nested = findNestedValue(direct as Record<string, unknown>, rest, visited);
    if (nested !== undefined) {
      return nested;
    }
  }

  for (const value of Object.values(source as Record<string, unknown>)) {
    if (value && typeof value === 'object') {
      const nested = findNestedValue(value as Record<string, unknown>, path, visited);
      if (nested !== undefined) {
        return nested;
      }
    }
  }

  return undefined;
}

function gatherSources(step: ReadyFlowStep): Array<Record<string, unknown> | Array<unknown>> {
  const sources: Array<Record<string, unknown> | Array<unknown>> = [];

  const pushSource = (value: unknown) => {
    if (value && typeof value === 'object') {
      sources.push(value as Record<string, unknown> | Array<unknown>);
    }
  };

  pushSource(step.response.extra);
  if (step.response.extra && typeof step.response.extra === 'object') {
    const extraRecord = step.response.extra as Record<string, unknown>;
    pushSource(extraRecord.dependencies);
    if (extraRecord.Unipesa && typeof extraRecord.Unipesa === 'object') {
      pushSource(extraRecord.Unipesa);
    }
  }
  pushSource(step.response.provider_extra);
  if ('action' in step && step.action) {
    pushSource(step.action.extra);
  }

  return sources;
}

function collectSummaryFields(step: ReadyFlowStep, locale: LocaleKey): SummaryFields {
  const sources = collectSummarySources(step);

  const amountNumeric = resolveNumberFromSources(sources, [
    'extra.Unipesa.amount',
    'Unipesa.amount',
    'amount',
    'amount_out',
    'amount_in'
  ]);
  const amountRaw = resolveStringFromSources(sources, [
    'extra.Unipesa.amount',
    'Unipesa.amount',
    'amount',
    'amount_out',
    'amount_in'
  ]);
  const currency = resolveStringFromSources(sources, [
    'extra.Unipesa.currency',
    'Unipesa.currency',
    'currency',
    'currency_out',
    'currency_in'
  ]);

  const primaryId = resolveStringFromSources(sources, ['order_id', 'orderID']);
  const secondaryId = resolveStringFromSources(sources, ['order_id2', 'orderID2']);
  const providerName = resolveStringFromSources(sources, [
    'provider_name',
    'provider.name',
    'extra.provider_name',
    'extra.provider.name',
    'Unipesa.provider_name',
    'provider'
  ]);
  const merchantName = resolveStringFromSources(sources, [
    'merchant_name',
    'merchant.name',
    'extra.merchant_name'
  ]);
  const paymentMethod = resolveStringFromSources(sources, [
    'payment_method',
    'method',
    'extra.payment_method',
    'extra.method'
  ]);
  const transactionType = resolveStringFromSources(sources, [
    'transaction_type',
    'transaction_type_id',
    'transaction.type',
    'extra.transaction_type',
    'extra.transaction_type_id',
    'Unipesa.transaction_type',
    'Unipesa.transaction_type_id'
  ]);
  const redirectUrl = resolveStringFromSources(sources, [
    'return_url',
    'extra.return_url',
    'payload_snapshot.return_url',
    'Unipesa.return_url'
  ]);

  const summary: SummaryFields = {};
  const amountDisplay = formatAmount(amountNumeric, amountRaw, currency, locale);
  if (amountDisplay) {
    summary.amountDisplay = amountDisplay;
  }
  if (currency) {
    summary.currency = currency;
  }
  if (primaryId) {
    summary.primaryId = primaryId;
  }
  if (secondaryId) {
    summary.secondaryId = secondaryId;
  }
  if (providerName) {
    summary.providerName = providerName;
  }
  if (merchantName) {
    summary.merchantName = merchantName;
  }
  if (paymentMethod) {
    summary.paymentMethod = paymentMethod;
  }
  if (transactionType) {
    summary.transactionType = transactionType;
  }
  if (redirectUrl) {
    summary.redirectUrl = redirectUrl;
  }

  return summary;
}

function collectSummarySources(step: ReadyFlowStep): Array<Record<string, unknown> | Array<unknown>> {
  const sources: Array<Record<string, unknown> | Array<unknown>> = [];

  const pushSource = (value: unknown) => {
    if (value && typeof value === 'object') {
      sources.push(value as Record<string, unknown> | Array<unknown>);
    }
  };

  pushSource(step.response);
  pushSource(step.response.extra);
  pushSource(step.response.provider_extra);
  pushSource(step.response.provider_result);
  pushSource(step.response.payload_snapshot);

  if (step.response.extra && typeof step.response.extra === 'object') {
    const extraRecord = step.response.extra as Record<string, unknown>;
    pushSource(extraRecord.Unipesa);
    pushSource(extraRecord.dependencies);
  }

  if (step.kind === 'form') {
    pushSource(step.action.extra);
  }

  return sources;
}

function resolveStringFromSources(
  sources: Array<Record<string, unknown> | Array<unknown>>,
  variants: string[]
): string | undefined {
  for (const source of sources) {
    for (const variant of variants) {
      const value = findNestedValue(source, variant.split('.'));
      if (isUsableValue(value)) {
        if (typeof value === 'string') {
          return value;
        }
        if (typeof value === 'number') {
          return String(value);
        }
      }
    }
  }
  return undefined;
}

function resolveNumberFromSources(
  sources: Array<Record<string, unknown> | Array<unknown>>,
  variants: string[]
): number | undefined {
  for (const source of sources) {
    for (const variant of variants) {
      const value = findNestedValue(source, variant.split('.'));
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string') {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
    }
  }
  return undefined;
}

function formatMessage(message: string): string {
  try {
    const parsed = JSON.parse(message);

    if (typeof parsed === 'string') {
      try {
        const nested = JSON.parse(parsed);
        return JSON.stringify(nested, null, 2);
      } catch {
        return parsed;
      }
    }

    return JSON.stringify(parsed, null, 2);
  } catch {
    const trimmed = message.trim();

    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const fallback = JSON.parse(trimmed);
        return JSON.stringify(fallback, null, 2);
      } catch {
        return message;
      }
    }

    const match = message.match(/\{[\s\S]*\}$/);
    if (match) {
      try {
        const fallback = JSON.parse(match[0]);
        return JSON.stringify(fallback, null, 2);
      } catch {
        return message;
      }
    }

    return message;
  }
}

function pickErrorMessage(entry: unknown): string {
  if (entry !== null && entry !== undefined && typeof entry === 'object') {
    if (Array.isArray(entry)) {
      return entry.map(pickErrorMessage).join(', ');
    }
    const record = entry as Record<string, unknown>;
    const candidate = record.message ?? record.error ?? record.code;
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
    return JSON.stringify(entry);
  }

  if (typeof entry === 'string') {
    return entry;
  }

  return String(entry);
}

